export interface Cursor { epoch: string; lastEventId: number; }
export interface RealtimeEvent { id: number; type: string; payload: unknown; serverNow: number; notification?: boolean; }
export type RealtimeStatus = "idle" | "connecting" | "syncing" | "open" | "reconnecting" | "closed";
interface Options {
  url(): string; socket(url: string): WebSocket; event(event: RealtimeEvent): void;
  status(status: RealtimeStatus, attempts: number): void; resync(): Promise<void>;
}

/** A socket belongs to one account lifetime. Snapshot replacement closes it before the read starts. */
export class RealtimeClient {
  private socket: WebSocket | null = null;
  private owner: string | null = null;
  private cursor: Cursor | null = null;
  private ticket = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private paused = true;
  constructor(private readonly options: Options) {}
  appliedCursor(): Cursor | null { return this.cursor ? { ...this.cursor } : null; }
  private detach(): void {
    this.ticket++;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const socket = this.socket; this.socket = null; socket?.close();
  }
  /** All changes after the read's cut will be replayed when its baseline is installed. */
  suspend(): void { this.paused = true; this.detach(); this.options.status("syncing", this.attempts); }
  baseline(owner: string, cursor: Cursor): void {
    if (!owner || typeof cursor.epoch !== "string" || !Number.isSafeInteger(cursor.lastEventId) || cursor.lastEventId < 0) throw new Error("Invalid snapshot cursor");
    this.detach(); this.owner = owner; this.cursor = { ...cursor }; this.paused = false; this.connect();
  }
  stop(): void {
    this.paused = true; this.owner = null; this.cursor = null; this.attempts = 0;
    this.detach(); this.options.status("closed", 0);
  }
  private schedule(task: () => void): void {
    const ticket = this.ticket; this.attempts++;
    this.options.status("reconnecting", this.attempts);
    this.timer = setTimeout(() => { this.timer = null; if (ticket === this.ticket && this.owner) task(); }, Math.min(10_000, 500 * 2 ** Math.min(this.attempts, 5)));
  }
  private resync(): void {
    this.suspend(); const pending = this.options.resync(), ticket = this.ticket;
    void pending.catch(() => {
      if (ticket === this.ticket && this.owner && this.paused) this.schedule(() => this.resync());
    });
  }
  private connect(): void {
    if (this.paused || !this.owner || !this.cursor || this.socket) return;
    const url = new URL(this.options.url());
    url.searchParams.set("epoch", this.cursor.epoch); url.searchParams.set("lastEventId", String(this.cursor.lastEventId));
    const socket = this.options.socket(url.toString()), ticket = ++this.ticket, owner = this.owner;
    this.socket = socket;
    const current = () => ticket === this.ticket && this.socket === socket && this.owner === owner && !this.paused;
    let catchup: { through: number; expected: number; received: number } | null = null;
    let ready = false;
    this.options.status(this.attempts ? "reconnecting" : "connecting", this.attempts);
    socket.onmessage = (message) => {
      if (!current()) return;
      let event: RealtimeEvent;
      try { event = JSON.parse(String(message.data)) as RealtimeEvent; } catch { this.resync(); return; }
      if (!event || !Number.isSafeInteger(event.id) || event.id < 0 || typeof event.type !== "string" || !Number.isFinite(event.serverNow)) { this.resync(); return; }
      if (event.type === "hello") {
        const p = event.payload as { accountId?: string; epoch?: string; lastEventId?: number; resume?: boolean; replayCount?: number } | null;
        if (ready || catchup || !p || p.accountId !== owner || p.epoch !== this.cursor!.epoch || p.resume !== true || !Number.isSafeInteger(p.lastEventId) || p.lastEventId! < this.cursor!.lastEventId || !Number.isSafeInteger(p.replayCount) || p.replayCount! < 0) { this.resync(); return; }
        catchup = { through: p.lastEventId!, expected: p.replayCount!, received: 0 }; return;
      }
      if (event.type === "ready") {
        const p = event.payload as { epoch?: string; lastEventId?: number; replayCount?: number } | null;
        if (!catchup || ready || !p || p.epoch !== this.cursor!.epoch || p.lastEventId !== catchup.through || p.replayCount !== catchup.expected || catchup.received !== catchup.expected) { this.resync(); return; }
        this.cursor!.lastEventId = catchup.through; ready = true; catchup = null; this.attempts = 0;
        this.options.status("open", 0); this.options.event(event); return;
      }
      if (event.id === 0) { if (ready && event.type === "pong") this.options.event(event); return; }
      if (!ready && (!catchup || event.id > catchup.through)) { this.resync(); return; }
      if (event.id <= this.cursor!.lastEventId) return;
      try { this.options.event(event); } catch { this.resync(); return; }
      if (!current()) return;
      this.cursor!.lastEventId = event.id;
      if (catchup) catchup.received++;
    };
    socket.onclose = () => { if (!current()) return; this.socket = null; this.schedule(() => this.connect()); };
    socket.onerror = () => { /* close drives retry; obsolete callbacks are ignored. */ };
  }
}
